import { useState } from 'react';
import { Box, Typography, Container, styled, keyframes } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useSearchParams } from 'react-router-dom';
import { DeviceMetricsPlots } from 'components/DeviceMetricsPlots';
import { DeviceSummary } from 'components/DeviceSummary';
import { ExportDeviceData } from 'components/ExportDeviceData';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DeviceAnalytics } from 'components/DeviceAnalytics';
import { Card } from '../components/ui/Card';
import { colors } from '../theme/colors';

export const DevicePage = () => {
  const [searchParams] = useSearchParams();
  const device_id = searchParams.get('device_id') as string;

  // Current date
  const currentDate = new Date();
  // Date 7 days ago
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  // State for date pickers
  const [startDate, setStartDate] = useState<Date>(sevenDaysAgo);
  const [endDate, setEndDate] = useState<Date>(currentDate);

  const fadeIn = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const Section = styled(Box)(({ delay = 0 }: { delay?: number }) => ({
    animation: `${fadeIn} 0.6s ease-out ${delay}ms forwards`,
    opacity: 0,
  }));

  const SectionHeader = ({ icon, title, delay = 0 }: { icon: React.ReactNode; title: string; delay?: number }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 3,
        animation: `${fadeIn} 0.6s ease-out ${delay}ms forwards`,
        opacity: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 2,
          background: colors.primary.gradient,
          color: colors.text.inverse,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Device Summary */}
        <Section delay={0} sx={{ mb: 4 }}>
          <DeviceSummary device_id={device_id} />
        </Section>

        {/* Hourly Analytics */}
        <Section delay={100} sx={{ mb: 4 }}>
          <SectionHeader icon={<AutoGraphIcon />} title="Почасовая аналитика" delay={100} />
          <Card variant="default" hover={false}>
            <Box sx={{ p: 3 }}>
              <DeviceAnalytics device_id={device_id} />
            </Box>
          </Card>
        </Section>

        {/* Full Metrics */}
        <Section delay={200} sx={{ mb: 4 }}>
          <SectionHeader icon={<AutoGraphIcon />} title="Полные показания" delay={200} />
          <Card variant="default" hover={false}>
            <Box sx={{ p: 3 }}>
              <DeviceMetricsPlots device_id={device_id} />
            </Box>
          </Card>
        </Section>

        {/* Data Export */}
        <Section delay={300}>
          <SectionHeader icon={<FileDownloadIcon />} title="Экспорт данных" delay={300} />
          <Card variant="default" hover={false}>
            <Box sx={{ p: 3 }}>
              <ExportDeviceData device_id={device_id} />
            </Box>
          </Card>
        </Section>
      </Container>
    </LocalizationProvider>
  );
};